// "Mute like this…" — builds a mute/notify rule from an email. The heavy
// lifting (sender domain, enforcement, per-user scope) is server-side; this is
// just a small builder seeded from the message, so one click can silence a
// recurring low-value sender (e.g. Wise spend alerts ≤ $25) without ever
// silencing the big ones.

import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BellOff, BellRing } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useThreadActivity } from '@/lib/collab';
import { cn } from '@/lib/utils';

type Ccy = 'USD' | 'GBP' | 'EUR';
const CCY_SYMBOL: Record<Ccy, string> = { USD: '$', GBP: '£', EUR: '€' };

interface DetectedAmount {
  value: number;
  currency: Ccy;
}

/** Categories worth offering a "mute them ALL" filter for — the useless-mail
 *  classes. Important/transactional are deliberately absent. */
const MUTABLE_CATEGORIES: Record<string, string> = {
  policy_update: 'policy / terms updates',
  noise: 'low-value notices',
  promo: 'promotional emails',
  newsletter: 'newsletters',
  cold_outreach: 'cold outreach',
  notification: 'automated notifications',
};

interface RuleLeafShape {
  field: string;
  op: string;
  value: string;
  currency?: Ccy;
  ai?: boolean;
}

/** An existing AI filter being edited (from the Settings → AI filters list). */
export interface EditableAiFilter {
  id: number;
  name: string;
  conditions: { all?: RuleLeafShape[]; any?: RuleLeafShape[] };
  actions: { type: string }[];
  scopeUserId: number | null;
}

/** The AI triage category of this thread, read from the provenance rows the
 *  triage worker writes — powers the "mute ALL emails like this" option. */
function useDetectedCategory(threadId: number | null): string | null {
  const q = useThreadActivity(threadId);
  return useMemo(() => {
    for (const e of q.data?.activity ?? []) {
      if (
        (e.action === 'ai.triage.classified' || e.action === 'ai.triage.muted') &&
        typeof e.meta.category === 'string'
      ) {
        return e.meta.category;
      }
    }
    return null;
  }, [q.data]);
}

/** Pull the most recent triage-detected amount from the thread's activity feed
 *  (the triage worker stores `amounts` in the audit meta). Returns the largest. */
function useDetectedAmount(threadId: number | null): DetectedAmount | null {
  const q = useThreadActivity(threadId);
  return useMemo(() => {
    for (const e of q.data?.activity ?? []) {
      const raw = (e.meta as { amounts?: unknown }).amounts;
      if (Array.isArray(raw) && raw.length > 0) {
        const parsed = raw
          .map((a) => a as { value?: unknown; currency?: unknown })
          .filter(
            (a): a is DetectedAmount =>
              typeof a.value === 'number' &&
              (a.currency === 'USD' || a.currency === 'GBP' || a.currency === 'EUR'),
          );
        if (parsed.length > 0) {
          return parsed.reduce((m, a) => (a.value > m.value ? a : m));
        }
      }
    }
    return null;
  }, [q.data]);
}

/** Round up with ~10% headroom to a clean multiple of 5, so a small price bump
 *  doesn't suddenly start pinging. 20→25, 60→70. */
function niceCeil(v: number): number {
  return Math.max(1, Math.ceil((v * 1.1) / 5) * 5);
}

export interface MuteRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null when editing an existing filter from Settings (no thread context). */
  threadId: number | null;
  messageId: number | null;
  senderDomain: string;
  /** Friendly default for the From field — sender name if present, else domain. */
  defaultFrom: string;
  /** The message's bare sender address — powers the "exact address" chip. */
  senderAddress?: string;
  /** Edit mode: rewrite this existing AI filter instead of creating one. */
  editRule?: EditableAiFilter | null;
}

export function MuteRuleDialog({
  open,
  onOpenChange,
  threadId,
  messageId,
  senderDomain,
  defaultFrom,
  senderAddress,
  editRule,
}: MuteRuleDialogProps) {
  const qc = useQueryClient();
  const detected = useDetectedAmount(threadId);
  const detectedCategory = useDetectedCategory(threadId);

  const [action, setAction] = useState<'mute' | 'notify'>('mute');
  const [scope, setScope] = useState<'me' | 'all'>('me');
  const [from, setFrom] = useState('');
  const [fromAi, setFromAi] = useState(false);
  // Match mode: this sender, or every email of the AI-detected category.
  const [matchMode, setMatchMode] = useState<'sender' | 'category'>('sender');
  const [category, setCategory] = useState<string | null>(null);
  const [useAmount, setUseAmount] = useState(false);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<Ccy>('USD');
  const [useKeyword, setUseKeyword] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [autoArchive, setAutoArchive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Live "what would this match" preview for the literal From word.
  const [preview, setPreview] = useState<{
    total: number;
    matches: number;
    distinctSenders: number;
    senders: string[];
  } | null>(null);
  useEffect(() => {
    if (!open || fromAi || matchMode !== 'sender') {
      setPreview(null);
      return;
    }
    const value = from.trim();
    if (!value) {
      setPreview(null);
      return;
    }
    const t = setTimeout(() => {
      fetch(`/api/rules/preview-from?value=${encodeURIComponent(value)}`, {
        credentials: 'include',
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => setPreview(j))
        .catch(() => setPreview(null));
    }, 350);
    return () => clearTimeout(t);
  }, [open, from, fromAi, matchMode]);

  // Seed each time the dialog opens: from the message, or from the filter
  // being edited.
  useEffect(() => {
    if (!open) return;
    if (editRule) {
      const leaves = editRule.conditions.all ?? editRule.conditions.any ?? [];
      const fromLeaf = leaves.find((l) => l.field === 'from');
      const amountLeaf = leaves.find((l) => l.field === 'amount');
      const textLeaf = leaves.find((l) => l.field === 'text');
      const catLeaf = leaves.find((l) => l.field === 'category');
      setAction(editRule.actions.some((a) => a.type === 'notify') ? 'notify' : 'mute');
      setScope(editRule.scopeUserId != null ? 'me' : 'all');
      setMatchMode(catLeaf ? 'category' : 'sender');
      setCategory(catLeaf?.value ?? null);
      setFrom(fromLeaf?.value ?? '');
      setFromAi(fromLeaf?.ai === true);
      setUseAmount(!!amountLeaf);
      setAmount(amountLeaf?.value ?? '');
      setCurrency(amountLeaf?.currency ?? 'USD');
      setUseKeyword(!!textLeaf);
      setKeyword(textLeaf?.value ?? '');
      setAutoArchive(editRule.actions.some((a) => a.type === 'archive'));
      return;
    }
    setAction('mute');
    setScope('me');
    setMatchMode('sender');
    setCategory(detectedCategory);
    // Default to the exact sender address — the safest match. The chips widen
    // it to the display name or whole domain deliberately, never by accident.
    setFrom(senderAddress || defaultFrom);
    setFromAi(false);
    setUseKeyword(false);
    setKeyword('');
    setAutoArchive(false);
    if (detected) {
      setUseAmount(true);
      setAmount(String(niceCeil(detected.value)));
      setCurrency(detected.currency);
    } else {
      setUseAmount(false);
      setAmount('');
      setCurrency('USD');
    }
  }, [open, detected, defaultFrom, senderAddress, detectedCategory, editRule]);

  const amountActive = action === 'mute' && useAmount;

  async function undo(ruleId: number) {
    try {
      await fetch(`/api/rules/mine/${ruleId}`, { method: 'DELETE', credentials: 'include' });
      void qc.invalidateQueries({ queryKey: ['rules'] });
      void qc.invalidateQueries({ queryKey: ['thread-activity', threadId] });
      toast.success('Rule removed');
    } catch {
      toast.error('Could not undo');
    }
  }

  const categoryMode = matchMode === 'category' && !!category;

  async function submit() {
    if (!messageId && !editRule) return;
    setSubmitting(true);
    try {
      const r = await fetch('/api/rules/from-message', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          messageId: messageId ?? undefined,
          ruleId: editRule?.id,
          action,
          scope,
          category: categoryMode ? category : null,
          from: categoryMode ? null : from.trim() || null,
          fromAi: categoryMode ? false : fromAi,
          maxAmount: !categoryMode && amountActive && amount ? Number(amount) : null,
          currency: !categoryMode && amountActive ? currency : null,
          contains: !categoryMode && useKeyword && keyword.trim() ? keyword.trim() : null,
          archive: action === 'mute' && autoArchive,
        }),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string; detail?: string };
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      const data = (await r.json()) as {
        id?: number;
        name?: string;
        alreadyExists?: boolean;
        updated?: boolean;
      };
      void qc.invalidateQueries({ queryKey: ['rules'] });
      if (threadId != null) void qc.invalidateQueries({ queryKey: ['thread-activity', threadId] });
      onOpenChange(false);

      const who = scope === 'me' ? 'you' : 'the team';
      const verb =
        action === 'mute' ? `won't notify ${who}` : `will always notify ${who}`;
      if (data.updated) {
        toast.success('AI filter updated', { description: data.name });
      } else if (data.alreadyExists) {
        toast.info(`Already have a filter like this — ${data.name}`);
      } else {
        toast.success(`Saved · mail like this ${verb}`, {
          description: data.name,
          action: data.id
            ? { label: 'Undo', onClick: () => void undo(data.id!) }
            : undefined,
        });
      }
    } catch {
      toast.error('Could not save the filter');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>AI filter</DialogTitle>
          <DialogDescription>
            Teach the inbox what to do with mail like this. Muting only skips the
            notification — the email still lands in the inbox.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Match mode — sender vs the whole AI-detected category. Only shown
              when the AI classified this thread as a mutable class (or when
              editing a category filter). */}
          {category && MUTABLE_CATEGORIES[category] && (
            <div>
              <div className="mb-1.5 text-xs font-medium text-muted-foreground">Match</div>
              <div className="grid grid-cols-1 gap-2">
                <Button
                  type="button"
                  variant={matchMode === 'sender' ? 'secondary' : 'outline'}
                  size="sm"
                  className="justify-start"
                  onClick={() => setMatchMode('sender')}
                >
                  Mail from this sender
                </Button>
                <Button
                  type="button"
                  variant={matchMode === 'category' ? 'secondary' : 'outline'}
                  size="sm"
                  className="justify-start"
                  onClick={() => setMatchMode('category')}
                >
                  ALL {MUTABLE_CATEGORIES[category]} — any sender
                </Button>
              </div>
              {matchMode === 'category' && (
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  Every email the AI classifies as {MUTABLE_CATEGORIES[category]} — e.g. "we've
                  updated our terms" blasts — will be {action === 'mute' ? 'muted' : 'notified'},
                  whoever sends it.
                </p>
              )}
            </div>
          )}

          {/* From — the sender match. A word/name, not necessarily an email. */}
          {!categoryMode && (
          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">From (sender)</div>
            <input
              type="text"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              placeholder={senderDomain || 'sender name, domain, or word'}
            />
            {!fromAi && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {[
                  senderAddress ? { label: 'Only this address', v: senderAddress } : null,
                  defaultFrom && defaultFrom !== senderAddress
                    ? { label: 'This name', v: defaultFrom }
                    : null,
                  senderDomain && senderDomain.includes('.')
                    ? { label: 'Whole domain', v: senderDomain }
                    : null,
                ]
                  .filter((c): c is { label: string; v: string } => !!c)
                  .map((c) => (
                    <button
                      key={c.label}
                      type="button"
                      onClick={() => setFrom(c.v)}
                      className={cn(
                        'rounded-full border px-2 py-0.5 text-[11px] transition-colors',
                        from.trim().toLowerCase() === c.v.toLowerCase()
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:bg-secondary',
                      )}
                    >
                      {c.label}
                    </button>
                  ))}
              </div>
            )}
            {!fromAi && preview && preview.total > 0 && (
              <p
                className={cn(
                  'mt-1.5 text-[11px]',
                  preview.matches / preview.total > 0.2
                    ? 'font-medium text-destructive'
                    : 'text-muted-foreground',
                )}
              >
                Matches {preview.matches} of your last {preview.total} emails
                {preview.distinctSenders > 0 && (
                  <>
                    {' '}from {preview.distinctSenders} sender
                    {preview.distinctSenders === 1 ? '' : 's'}
                    {preview.distinctSenders > 1 && preview.senders.length > 0 && (
                      <> — e.g. {preview.senders.slice(0, 2).join(', ')}</>
                    )}
                  </>
                )}
                {preview.matches / preview.total > 0.2 && ' — probably too broad'}
              </p>
            )}
            <label className="mt-2 flex items-start gap-2 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={fromAi}
                onCheckedChange={(c) => setFromAi(c === true)}
              />
              <span>
                Let AI interpret this
                <span className="block text-[11px] text-muted-foreground">
                  {fromAi
                    ? 'Describe the sender in plain words — e.g. "any payment provider", "Google billing". The AI decides if each sender fits (one quick check per email).'
                    : 'Off: matches the text above literally against the sender. On: treat it as a description the AI judges.'}
                </span>
              </span>
            </label>
          </div>
          )}

          {/* Action: mute vs always-notify */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={action === 'mute' ? 'default' : 'outline'}
              className="justify-start gap-2"
              onClick={() => setAction('mute')}
            >
              <BellOff className="size-4" strokeWidth={1.75} />
              Mute
            </Button>
            <Button
              type="button"
              variant={action === 'notify' ? 'default' : 'outline'}
              className="justify-start gap-2"
              onClick={() => setAction('notify')}
            >
              <BellRing className="size-4" strokeWidth={1.75} />
              Always notify
            </Button>
          </div>

          {/* Scope: me vs everyone */}
          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">Applies to</div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={scope === 'me' ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setScope('me')}
              >
                Just me
              </Button>
              <Button
                type="button"
                variant={scope === 'all' ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setScope('all')}
              >
                Everyone on this inbox
              </Button>
            </div>
          </div>

          {/* Auto-archive — mute-only. Archiving is a shared-inbox side effect,
              so enabling it forces workspace scope. */}
          {action === 'mute' && (
            <div className="rounded-md border p-3">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={autoArchive}
                  onCheckedChange={(c) => {
                    const on = c === true;
                    setAutoArchive(on);
                    if (on) setScope('all');
                  }}
                />
                Also auto-archive matching emails
              </label>
              <p className="mt-1.5 pl-6 text-xs text-muted-foreground">
                Marked read and moved out of the Inbox for everyone (still in All
                Mail){autoArchive ? ' — requires "Everyone on this inbox"' : ''}.
              </p>
            </div>
          )}

          {/* Amount ceiling — only meaningful for mute */}
          {!categoryMode && action === 'mute' && (
            <div className="rounded-md border p-3">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={useAmount}
                  onCheckedChange={(c) => setUseAmount(c === true)}
                />
                Only when the amount is at or below
              </label>
              <div
                className={cn(
                  'mt-2 flex items-center gap-2 pl-6',
                  !useAmount && 'pointer-events-none opacity-50',
                )}
              >
                <Select value={currency} onValueChange={(v) => setCurrency(v as Ccy)}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">$ USD</SelectItem>
                    <SelectItem value="GBP">£ GBP</SelectItem>
                    <SelectItem value="EUR">€ EUR</SelectItem>
                  </SelectContent>
                </Select>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="any"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="h-9 w-28 rounded-md border bg-transparent px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                  placeholder="25"
                />
                {detected && (
                  <span className="text-xs text-muted-foreground">
                    detected {CCY_SYMBOL[detected.currency]}
                    {detected.value}
                  </span>
                )}
              </div>
              <p className="mt-1.5 pl-6 text-[11px] text-muted-foreground">
                Bigger amounts still notify — so a large or unexpected charge always
                reaches you.
              </p>
            </div>
          )}

          {/* Optional keyword */}
          {!categoryMode && (
          <div className="rounded-md border p-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={useKeyword}
                onCheckedChange={(c) => setUseKeyword(c === true)}
              />
              Only when subject or body contains
            </label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              disabled={!useKeyword}
              className={cn(
                'mt-2 ml-6 h-9 w-[calc(100%-1.5rem)] rounded-md border bg-transparent px-3 text-sm outline-none focus:ring-2 focus:ring-ring',
                !useKeyword && 'pointer-events-none opacity-50',
              )}
              placeholder='e.g. "Google Workspace"'
            />
          </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={
              submitting ||
              (!messageId && !editRule) ||
              (!categoryMode && !from.trim())
            }
          >
            {editRule
              ? 'Save filter'
              : action === 'mute'
                ? categoryMode
                  ? 'Mute them all'
                  : 'Mute mail like this'
                : 'Always notify'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
